import { t } from "@bn-games/i18n-core";
import {
  acceptInvite,
  declineInvite,
  gameLabel,
  gameWebPath,
  type AcceptedInvite,
  type Invite,
} from "@bn-games/social-client";
import { clear, el } from "../components/dom";
import { getLandingUrl } from "../components/landingUrl";
import { openRoomLink } from "../components/roomNavigation";
import { confirmDialog } from "../dialogs";
import { announce } from "../LiveRegion";
import { isEmbeddedShell } from "./embedGating";
import { createFootprintEmitter, type FootprintListener } from "./footprint";
import { createInviteCard } from "./InviteCard";
import { createInviteFeed } from "./inviteFeed";
import { createInvitePill } from "./InvitePill";

export type InviteDockDensity = "expanded" | "compact";

export interface MountInviteDockOptions {
  /** WS URL of `/ws/lobby`. Without it the dock still works, on wake polling alone. */
  readonly serverUrl?: string;
  readonly density?: InviteDockDensity;
  /** Consulted on Accept: true asks before navigating away from the game. */
  readonly isGameInProgress?: () => boolean;
}

export interface InviteDockHandle {
  setDensity(density: InviteDockDensity): void;
  /** Listener gets the pill's box while compact and visible, else `null`; called once on subscribe. */
  onCompactFootprintChange(listener: FootprintListener): () => void;
  destroy(): void;
}

const NOOP_DOCK: InviteDockHandle = {
  setDensity(): void {},
  onCompactFootprintChange: () => () => {},
  destroy(): void {},
};

function roomUrl(accepted: AcceptedInvite): string {
  const code = encodeURIComponent(accepted.roomCode);
  return `${getLandingUrl()}/${gameWebPath(accepted.game)}/#room=${code}`;
}

/**
 * Mounts the page's pending-invite dock into `document.body`. `expanded` is
 * the top-centre card for menus; `compact` is a small top-left pill for
 * everywhere else in a game. The list itself comes from `createInviteFeed`.
 * Renders nothing inside a Discord Activity or a Capacitor native shell.
 */
export function mountInviteDock(options: MountInviteDockOptions = {}): InviteDockHandle {
  if (isEmbeddedShell()) return NOOP_DOCK;

  const isGameInProgress = options.isGameInProgress ?? ((): boolean => false);
  let density: InviteDockDensity = options.density ?? "expanded";

  const banner = el("div", { class: "invite-dock__banner" });
  const pill = createInvitePill();
  const dock = el("div", { class: "invite-dock" }, [banner, pill.root]);
  document.body.appendChild(dock);

  const footprint = createFootprintEmitter();
  const announced = new Set<string>();
  let invites: readonly Invite[] = [];
  let busy = false;
  let error: string | null = null;
  let destroyed = false;

  // Only the newest invite is shown, with a count of the rest, so the dock
  // never grows into a stack covering what's behind it.
  function render(): void {
    const ordered = [...invites].sort((a, b) => b.createdAt - a.createdAt);
    const [current] = ordered;
    clear(banner);
    const card =
      current === undefined
        ? null
        : createInviteCard({
            invite: current,
            moreCount: ordered.length - 1,
            busy,
            error,
            onAccept: () => void onAccept(current),
            onDecline: () => void onDecline(current),
          });
    const expanded = density === "expanded" && card !== null;
    banner.classList.toggle("invite-dock__banner--visible", expanded);
    if (expanded) banner.appendChild(card);
    if (density === "compact" && current !== undefined) pill.update(current, ordered.length, card);
    else pill.update(null, 0, null);
    footprint.report(pill.footprint());
  }

  // Announced once per invite, never by moving focus: a player mid-move must
  // not lose their place on the board to a notification.
  function announceArrival(): void {
    const fresh = invites.filter((invite) => !announced.has(invite.id));
    for (const invite of fresh) announced.add(invite.id);
    const newest = [...fresh].sort((a, b) => b.createdAt - a.createdAt)[0];
    if (newest === undefined) return;
    announce(t("social.invite.message", { sender: newest.from.displayName, game: gameLabel(newest.game) }));
  }

  async function onAccept(invite: Invite): Promise<void> {
    if (busy) return;
    if (isGameInProgress()) {
      pill.close(true);
      const leave = await confirmDialog({
        title: t("social.dock.leaveGameTitle"),
        message: t("social.dock.leaveGameBody", { game: gameLabel(invite.game) }),
        confirmLabel: t("social.dock.leaveGameConfirm"),
        cancelLabel: t("social.dock.leaveGameCancel"),
      });
      if (!leave || destroyed || busy) return;
    }
    busy = true;
    error = null;
    render();
    const result = await acceptInvite(invite.id);
    if (destroyed) return;
    busy = false;
    if (result.ok) {
      // A full load even for the game we're already in: that's what re-runs
      // this app's own `#room=CODE` resolution.
      openRoomLink(roomUrl(result.value));
      return;
    }
    // Left in place rather than dropped: an expired or already-answered invite
    // disappears on the next refresh, which is the server's answer, not a guess.
    error = result.error.message;
    render();
  }

  async function onDecline(invite: Invite): Promise<void> {
    if (busy) return;
    busy = true;
    error = null;
    render();
    const result = await declineInvite(invite.id);
    if (destroyed) return;
    busy = false;
    if (result.ok) invites = invites.filter((pending) => pending.id !== invite.id);
    else error = result.error.message;
    render();
  }

  const onResize = (): void => footprint.report(pill.footprint());
  window.addEventListener("resize", onResize);
  const feed = createInviteFeed({
    ...(options.serverUrl !== undefined ? { serverUrl: options.serverUrl } : {}),
    onInvites: (next) => {
      invites = next;
      error = null;
      announceArrival();
      render();
    },
  });

  return {
    setDensity(next): void {
      if (destroyed || next === density) return;
      density = next;
      render();
    },
    onCompactFootprintChange: (listener) => footprint.subscribe(listener),
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      feed.destroy();
      window.removeEventListener("resize", onResize);
      pill.destroy();
      dock.remove();
      footprint.report(null);
      footprint.clear();
    },
  };
}
